import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { RemoteFilePicker } from "./agent-v2/RemoteFilePicker.js";
const hostOptionKey = (h) => `${h.kind}:${h.id}`;
const MODEL_OPTIONS = [
    { value: "", label: "Default (Hub-selected)" },
    { value: "claude-opus-4-7", label: "Opus 4.7" },
    { value: "claude-sonnet-4-6", label: "Sonnet 4.6" },
    { value: "claude-haiku-4-5", label: "Haiku 4.5" },
];
/**
 * New-agent flow for the Web shell. Walks the user through host pick
 * (pty daemons and unified agent-runtime hosts, merged) → working-directory
 * pick (server-side fs traversal, routed by host kind) → name + model +
 * options, then fires createAgent + startAgent back-to-back and calls
 * `onCreated` with the fresh record so the shell can navigate into the
 * terminal.
 */
export function CreateAgentDialog({ workspaceId, onClose, onCreated }) {
    const ctx = useDataContext();
    const [hosts, setHosts] = useState(null);
    const [hostKey, setHostKey] = useState(null);
    const [name, setName] = useState("");
    const [model, setModel] = useState("");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [skipPermissions, setSkipPermissions] = useState(false);
    const [continueSession, setContinueSession] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [runtimeListWarning, setRuntimeListWarning] = useState(null);
    // Host list — refresh once on mount; user picks which daemon runs the
    // agent. Both families are offered since KTK-343: pty daemons and unified
    // agent-runtime hosts can each run a Claude Code agent. The runtime fetch
    // degrades to an empty list rather than failing the whole picker — a Hub
    // predating the endpoint shouldn't take pty creation down with it — but the
    // failure surfaces as an inline warning: a silently missing host family
    // reads as "that machine isn't registered" and sends the user (and the
    // team) debugging the wrong layer.
    useEffect(() => {
        let cancelled = false;
        let runtimeError = null;
        Promise.all([
            ctx.hub.listPtyHosts(),
            ctx.hub.listAgentRuntimeHosts().catch((e) => {
                runtimeError = e instanceof Error ? e.message : String(e);
                return [];
            }),
        ])
            .then(([pty, runtime]) => {
            if (cancelled)
                return;
            setRuntimeListWarning(runtimeError);
            const merged = [
                ...pty
                    .filter((h) => !h.revoked_at)
                    .map((h) => ({
                    kind: "pty",
                    id: h.host_id,
                    display_name: h.display_name,
                    platform: h.platform,
                    neverSeen: !h.last_seen_at,
                    claudeSignedOut: false,
                })),
                ...runtime.map((h) => ({
                    kind: "runtime",
                    id: h.host_id,
                    display_name: h.display_name,
                    platform: h.platform,
                    neverSeen: !h.last_seen_at,
                    claudeSignedOut: h.claude_logged_in === false,
                })),
            ];
            setHosts(merged);
            setHostKey((prev) => prev ?? (merged[0] ? hostOptionKey(merged[0]) : null));
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub]);
    const selectedHost = useMemo(() => hosts?.find((h) => hostOptionKey(h) === hostKey) ?? null, [hosts, hostKey]);
    // Separator depends on the host OS (backend reports it in fs:list too, but
    // the first render of the picker happens before we've probed anything).
    const fallbackSeparator = selectedHost?.platform === "win32" ? "\\" : "/";
    // The cwd picker holds the chosen path; typing in the field is allowed but
    // navigation is driven by the list below.
    const [workingDir, setWorkingDir] = useState("~");
    // KTK-212 fix — this component is now body-only. The tabbed
    // AddAgentDialog supplies its own modal chrome (backdrop, sizing,
    // dismissal); CreateAgentDialog used to render its own
    // fixed-inset-0 backdrop too, which sat on top of the tab strip
    // and hid both tabs. Callers that want a standalone modal wrap the
    // body in their own backdrop / portal.
    return (_jsx(_Fragment, { children: _jsxs("form", { onSubmit: async (e) => {
                e.preventDefault();
                if (submitting)
                    return;
                const trimmedName = name.trim();
                if (!trimmedName) {
                    setError("Agent name is required.");
                    return;
                }
                if (!workingDir.trim()) {
                    setError("Pick a working directory.");
                    return;
                }
                if (!selectedHost) {
                    setError("Pick a host.");
                    return;
                }
                setSubmitting(true);
                setError(null);
                try {
                    const agent = await ctx.hub.createAgent({
                        workspace_id: workspaceId,
                        name: trimmedName,
                        working_dir: workingDir.trim(),
                        system_prompt: systemPrompt.trim() || undefined,
                        model: model || undefined,
                        dangerously_skip_permissions: skipPermissions,
                        continue_session: continueSession,
                        host_id: selectedHost.id,
                    });
                    await ctx.hub.startAgent(workspaceId, agent.id, { continue: continueSession });
                    onCreated(agent);
                    onClose();
                }
                catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                    setSubmitting(false);
                }
            }, style: {
                // No background / border / padding here — the tabbed
                // AddAgentDialog body provides those at the wrapper level
                // so the tab strip's hairline meets the form cleanly.
                display: "flex",
                flexDirection: "column",
                gap: 14,
            }, children: [_jsx("h2", { style: { margin: 0, fontSize: 16, color: "var(--text-primary)" }, children: "New agent" }), _jsxs("label", { style: labelStyle, children: [_jsx("span", { style: labelTextStyle, children: "Host" }), hosts === null ? (_jsx("div", { style: { ...inputStyle, color: "var(--text-muted)" }, children: "Loading hosts\u2026" })) : hosts.length === 0 ? (_jsx("div", { style: { ...inputStyle, color: "var(--text-muted)" }, children: "No hosts registered for your account." })) : (_jsx("select", { value: hostKey ?? "", onChange: (e) => {
                                setHostKey(e.target.value || null);
                                // Reset cwd when the host changes — a /Users/... path makes no
                                // sense on a Windows host and vice versa.
                                setWorkingDir("~");
                            }, style: inputStyle, children: hosts.map((h) => (_jsxs("option", { value: hostOptionKey(h), children: [h.display_name, " \u00B7 ", platformLabel(h.platform), h.kind === "runtime" ? " · runtime" : "", h.neverSeen ? " · never seen" : "", h.claudeSignedOut ? " · Claude not signed in" : ""] }, hostOptionKey(h)))) })), runtimeListWarning && (_jsxs("div", { style: { marginTop: 4, fontSize: 12, color: "var(--error)", lineHeight: 1.4 }, children: ["Runtime hosts couldn't be loaded \u2014 this list shows PTY daemon hosts only. A runtime-installed machine missing here is likely this, not an unregistered host. (", runtimeListWarning, ")"] }))] }), _jsxs("label", { style: labelStyle, children: [_jsx("span", { style: labelTextStyle, children: "Working directory" }), _jsx("input", { type: "text", value: workingDir, onChange: (e) => setWorkingDir(e.target.value), placeholder: selectedHost?.platform === "win32" ? "C:\\Users\\you" : "~", style: inputStyle, spellCheck: false })] }), selectedHost && (_jsx(RemoteFilePicker, { hostId: selectedHost.id, hostKind: selectedHost.kind, workspaceId: workspaceId, initialPath: workingDir, separator: fallbackSeparator, onSelect: setWorkingDir })), _jsxs("label", { style: labelStyle, children: [_jsx("span", { style: labelTextStyle, children: "Name" }), _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "river", required: true, pattern: "[a-z0-9-]+", title: "Lowercase letters, numbers, and hyphens only", style: inputStyle })] }), _jsxs("label", { style: labelStyle, children: [_jsx("span", { style: labelTextStyle, children: "Model" }), _jsx("select", { value: model, onChange: (e) => setModel(e.target.value), style: inputStyle, children: MODEL_OPTIONS.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }), _jsxs("label", { style: labelStyle, children: [_jsx("span", { style: labelTextStyle, children: "System prompt (optional)" }), _jsx("textarea", { value: systemPrompt, onChange: (e) => setSystemPrompt(e.target.value), rows: 4, style: { ...inputStyle, fontFamily: "inherit", resize: "vertical" }, placeholder: "Additional instructions appended to the base agent prompt." })] }), _jsxs("label", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                    }, children: [_jsx("input", { type: "checkbox", checked: skipPermissions, onChange: (e) => setSkipPermissions(e.target.checked) }), "Skip Claude Code permission prompts", _jsx("span", { style: { color: "var(--text-muted)", fontSize: 11 }, children: "(--dangerously-\u2026)" })] }), _jsxs("label", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                    }, children: [_jsx("input", { type: "checkbox", checked: continueSession, onChange: (e) => setContinueSession(e.target.checked) }), "Resume the prior session", _jsx("span", { style: { color: "var(--text-muted)", fontSize: 11 }, children: "(--continue)" })] }), error && _jsx("div", { style: errorBox, children: error }), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8 }, children: [_jsx("button", { type: "button", onClick: onClose, style: { ...btnStyle, background: "transparent", color: "var(--text-secondary)" }, children: "Cancel" }), _jsx("button", { type: "submit", disabled: submitting || !name.trim() || !selectedHost, style: {
                                ...btnStyle,
                                background: submitting || !name.trim() || !selectedHost ? "var(--bg-hover)" : "var(--accent)",
                                color: "white",
                                cursor: submitting || !name.trim() || !selectedHost ? "not-allowed" : "pointer",
                            }, children: submitting ? "Creating…" : "Create & start" })] })] }) }));
}
function platformLabel(p) {
    if (p === "darwin")
        return "macOS";
    if (p === "win32")
        return "Windows";
    if (p === "linux")
        return "Linux";
    return p;
}
const labelStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
};
const labelTextStyle = {
    fontSize: 11,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
};
const inputStyle = {
    padding: "8px 12px",
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
};
const btnStyle = {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
};
const errorBox = {
    color: "var(--error)",
    fontSize: 12,
    padding: 8,
    border: "1px solid rgba(250,82,82,0.3)",
    borderRadius: 6,
};
//# sourceMappingURL=CreateAgentDialog.js.map